import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

interface FaqItem {
  q: string;
  a: string;
  tag?: string;
  link?: { label: string; href: string };
}

// Mission Beach Business FAQ — Entity-First, AI-scrapable answers
const missionBeachFaqs: FaqItem[] = [
  {
    q: 'How can Mission Beach businesses improve local search visibility?',
    tag: 'Mission Beach SEO',
    a: 'Mission Beach businesses improve local search visibility through three core actions: First, claim and fully optimise your Google Business Profile with accurate NAP (Name, Address, Phone) data, business hours, and high-quality photos of your location. Second, build consistent local citations across Australian directories including TrueLocal, Yelp Australia, and industry-specific listings. Third, create location-specific content that mentions Mission Beach, the Cassowary Coast, and nearby landmarks naturally within your website copy. Darkside Digital implements all three for Mission Beach businesses as part of our Local SEO service.',
  },
  {
    q: 'What is the best SEO strategy for FNQ tourism and retail?',
    tag: 'FNQ Tourism SEO',
    a: 'The best SEO strategy for Far North Queensland tourism and retail combines local search optimisation with experience-focused content. For FNQ tourism operators, this means targeting "near me" searches, optimising for mobile (75% of regional searches happen on mobile), and creating content that answers specific traveller questions like "best reef tours Mission Beach" or "where to stay Cassowary Coast." For retail, focus on Google Business Profile optimisation, local inventory showing in Google Search, and review generation. The key difference for FNQ is accounting for seasonal tourism cycles and mobile network speeds — your site must load fast on regional 4G.',
  },
  {
    q: 'Why should Mission Beach businesses care about AI search and GEO?',
    tag: 'AI Search',
    a: 'Mission Beach businesses should care about AI search and Generative Engine Optimisation (GEO) because the way customers find businesses is fundamentally changing. When a traveller asks ChatGPT "what is the best restaurant in Mission Beach" or "which dive operator should I book in Far North Queensland," AI engines give one confident recommendation — not a list of 10 links. If your business is not the one cited, you are invisible to that customer. GEO ensures your Mission Beach business becomes the authoritative answer AI engines trust and recommend.',
  },
  {
    q: 'How is digital marketing different for regional Queensland businesses?',
    tag: 'Regional Marketing',
    a: 'Digital marketing for regional Queensland businesses differs from metro strategies in four key ways: Network speed — sites must load fast on regional 4G and satellite connections (we target LCP under 1.2s). Mobile dominance — regional users rely more heavily on mobile search. Local trust signals — regional customers prioritise local ownership and community connection, which must be reflected in your content. Seasonal cycles — FNQ tourism and retail face distinct wet/dry season patterns that affect search behaviour and campaign timing. Darkside Digital, based in Mission Beach, builds these regional factors into every strategy.',
  },
  {
    q: 'What makes Darkside Digital the Mission Beach digital authority?',
    tag: 'About Us',
    a: 'Darkside Digital is the Mission Beach digital authority because we are the only full-service digital agency physically based in the Cassowary Coast region. We understand the Mission Beach market, the seasonal tourism cycles, the regional mobile network constraints, and the specific search behaviour of FNQ customers. We combine this local knowledge with technical expertise in AI search optimisation (GEO), local SEO, and conversion-focused web design. Our client base spans Mission Beach, Innisfail, Tully, Cardwell, and Cairns — we know this market because we are part of it.',
  },
  {
    q: 'How long does local SEO take to work for Cassowary Coast businesses?',
    tag: 'Timeline',
    a: 'Local SEO for Cassowary Coast businesses typically shows meaningful results within 60 to 90 days. Google needs time to crawl, index, and evaluate your site against local competitors. Initial improvements — like Google Business Profile optimisation — can show impact within 2 to 4 weeks. Ranking movement for competitive keywords in Mission Beach, Innisfail, and surrounding areas usually appears around the 60-day mark. Full local dominance, particularly for tourism operators competing in the broader FNQ market, typically requires 4 to 6 months of consistent optimisation.',
  },
  {
    q: 'Do I need a new website or can you fix my existing one?',
    tag: 'Website',
    a: 'We can fix most existing websites if the foundation is sound. Our process starts with a comprehensive audit that tells you exactly what is worth keeping, what needs fixing, and what is actively hurting your rankings. For Mission Beach businesses with outdated sites, we often recommend a targeted rebuild of key pages rather than a full new site — this delivers faster results at lower cost. If your current site is built on a restrictive platform, has technical debt, or performs poorly on mobile, a fresh build is usually the smarter long-term investment.',
  },
  {
    q: 'What is GEO and how is it different from SEO?',
    tag: 'GEO / AEO',
    a: 'GEO (Generative Engine Optimisation) is the practice of making your business visible inside AI-generated answers. SEO gets you found on Google Search. GEO gets your business cited by AI tools like ChatGPT, Google Gemini, and Perplexity when customers ask questions. They require completely different strategies: SEO rewards backlinks and keyword density; GEO rewards clear, structured, authoritative answers and entity signals. Being number one on Google does not mean AI will cite you — the overlap has dropped below 20%. Mission Beach businesses need both to capture every customer.',
    link: { label: 'Full GEO / AEO breakdown', href: '/geo-aeo' },
  },
  {
    q: 'How do you measure results for Mission Beach businesses?',
    tag: 'Reporting',
    a: 'We measure results for Mission Beach businesses through clear, no-jargon monthly reports covering: Local search rankings for target keywords in the Cassowary Coast and FNQ region; Google Business Profile insights — views, searches, and actions; website traffic and conversion rates; AI visibility metrics for GEO clients — citation rate, brand mention frequency, and share of voice across ChatGPT, Gemini, and Perplexity. Every metric ties directly to business outcomes: phone calls, bookings, enquiries, and revenue.',
  },
  {
    q: 'Do you work with businesses outside Mission Beach and FNQ?',
    tag: 'Service Area',
    a: 'Yes. While Mission Beach and Far North Queensland are our home base and specialty, we work with businesses across Australia. Our GEO and AI search services are location-agnostic — we have clients in Brisbane, Sydney, Melbourne, and regional areas nationwide. For local SEO, our expertise is sharpest in Australian markets where we understand the directory landscape, search behaviour, and competitive dynamics. If you are outside Australia, reach out — we will be upfront about whether we are the right fit.',
  },
  {
    q: 'What do you need from me to get started?',
    tag: 'Process',
    a: 'To get started, we need: 30 minutes for an initial strategy call to understand your Mission Beach business and goals; access to your existing accounts — Google Business Profile, website backend, hosting, and any ad accounts; your brand assets — logo, brand colours, and any existing marketing materials. You do not need to have everything perfect. We have worked with Mission Beach businesses that started with nothing but a phone number and built their entire digital presence from scratch.',
  },
  {
    q: 'Is there a contract or can I cancel anytime?',
    tag: 'Commitment',
    a: 'No lock-in contracts. Ever. We operate month-to-month once the initial project is delivered. If we are not delivering results, you should not be stuck paying for it. We are confident enough in our work that we do not need to trap Mission Beach clients. Most stay for years, but that is their choice, not a clause in a document.',
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const groupRef = useScrollRevealGroup();

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  const mid = Math.ceil(missionBeachFaqs.length / 2);
  const leftCol = missionBeachFaqs.slice(0, mid);
  const rightCol = missionBeachFaqs.slice(mid);

  return (
    <section id="faq" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      {/* Grid */}
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      {/* Glow */}
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] -translate-y-1/2 bg-ds-red/5 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label">Mission Beach Business FAQ</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              DEFINITIVE
              <br />
              <span className="text-ds-red neon-text-red">ANSWERS.</span>
            </h2>
            <p className="font-body text-white/45 text-sm leading-relaxed max-w-md lg:text-right">
              Straight answers to the questions Mission Beach and FNQ businesses actually ask.
              <br />
              <span className="text-white/70">Still got one? Email{' '}</span>
              <a
                href="mailto:hello@darksidedigital.com.au"
                className="text-ds-red hover:text-white transition-colors duration-200"
              >
                hello@darksidedigital.com.au
              </a>
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Two-column FAQ grid */}
        <div className="grid lg:grid-cols-2 gap-4 reveal">

          {/* Left column */}
          <div className="space-y-2">
            {leftCol.map((faq, i) => (
              <FaqRow
                key={faq.q}
                faq={faq}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => toggle(i)}
              />
            ))}
          </div>

          {/* Right column */}
          <div className="space-y-2">
            {rightCol.map((faq, i) => {
              const realIndex = i + mid;
              return (
                <FaqRow
                  key={faq.q}
                  faq={faq}
                  index={realIndex}
                  isOpen={openIndex === realIndex}
                  onToggle={() => toggle(realIndex)}
                />
              );
            })}
          </div>
        </div>

        {/* Bottom strip */}
        <div
          className="mt-16 border border-ds-border bg-ds-bg p-10 flex flex-col md:flex-row items-start justify-between gap-8 reveal"
          style={{ borderLeft: '3px solid #B4FF39' }}
        >
          <div>
            <h3 className="font-display text-2xl text-white mb-1 tracking-wide">
              STILL GOT A QUESTION?
            </h3>
            <p className="font-body text-white/45 text-sm leading-relaxed">
              Drop us a message and we will come back to you within a few hours — in plain English, no sales speak.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
            <a
              href="mailto:hello@darksidedigital.com.au"
              className="btn-outline text-xs flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-mail-line" />
              Email Us
            </a>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-red text-xs whitespace-nowrap"
            >
              Book a Free Strategy Call
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}

interface FaqRowProps {
  faq: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqRow({ faq, isOpen, onToggle }: FaqRowProps) {
  return (
    <div
      className={`border transition-all duration-300 cursor-pointer ${
        isOpen
          ? 'border-ds-red/40 bg-ds-bg'
          : 'border-ds-border bg-ds-bg hover:border-white/20'
      }`}
      style={isOpen ? { boxShadow: '0 0 30px rgba(180,255,57,0.06)' } : {}}
      onClick={onToggle}
    >
      {/* Question row */}
      <div className="flex items-center justify-between gap-4 px-6 py-5">
        <div className="flex items-center gap-4 min-w-0">
          {faq.tag && (
            <span
              className={`font-body text-xs tracking-widest uppercase px-2 py-0.5 border flex-shrink-0 transition-all duration-300 ${
                isOpen
                  ? 'border-ds-red/50 text-ds-red bg-ds-red/8'
                  : 'border-ds-border text-white/30'
              }`}
            >
              {faq.tag}
            </span>
          )}
          <h3
            className={`font-heading font-700 text-sm leading-snug transition-colors duration-300 ${
              isOpen ? 'text-white' : 'text-white/75'
            }`}
          >
            {faq.q}
          </h3>
        </div>

        {/* Toggle icon */}
        <div
          className={`w-7 h-7 flex items-center justify-center border flex-shrink-0 transition-all duration-300 ${
            isOpen
              ? 'border-ds-red/50 bg-ds-red/10 rotate-45'
              : 'border-ds-border rotate-0'
          }`}
        >
          <i
            className={`ri-add-line text-sm transition-colors duration-300 ${
              isOpen ? 'text-ds-red' : 'text-white/40'
            }`}
          />
        </div>
      </div>

      {/* Answer — animated open/close */}
      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 pt-0">
          <div className="border-t border-ds-border/50 pt-4 space-y-3">
            <p className="font-body text-white/70 text-sm leading-relaxed">
              {faq.a}
            </p>
            {faq.link && (
              <Link
                to={faq.link.href}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 font-body text-xs text-ds-red hover:text-white transition-colors duration-200 group"
              >
                <span>{faq.link.label}</span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-150" />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
